import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Copy, Check, Wand2, RefreshCw, ChevronDown, ChevronUp, Users, X, ImagePlus } from 'lucide-react';
import { extractAPI } from '../lib/api';

// ── Applicant line builders ───────────────────────────────────────────────
function applicantLines(count) {
  if (count === 1) return '';
  const ordinals = ['Second', 'Third', 'Fourth'];
  const lines = [];
  for (let i = 2; i <= count; i++) {
    lines.push(
      `Applicant ${i} is [${ordinals[i - 2].toUpperCase()} APPLICANT NAME], employed as a [JOB TITLE ${i}] since [DATE ${i}], with an annual income of £[INCOME ${i}].`
    );
  }
  return '\n\n' + lines.join('\n\n');
}

// ── Suitability template factory ──────────────────────────────────────────
function buildSuitabilityTemplates(applicants) {
  const extra = applicantLines(applicants);
  const incomeNote = applicants > 1
    ? ` You are applying jointly, with a combined annual income of £[COMBINED INCOME].`
    : '';

  return {
    purchase_ftb: {
      label: 'Purchase — First-Time Buyer',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      sections: {
        current_situation:
`You are looking to purchase your first residential property for £[PROPERTY VALUE], with a deposit of £[DEPOSIT] sourced from [SOURCE OF FUNDS].

You are currently employed as a [JOB TITLE], a role you have held since [DATE], with an annual income of £[INCOME].${extra}${incomeNote}

You are [MARITAL STATUS] with [NUMBER] dependants.

This purchase is being made through [estate agent / developer / private sale] and will be your main residence.`,
        client_objectives:
`Your primary objective is to secure a mortgage that provides stability in your monthly payments, with a preference for a [2/3/5]-year fixed rate product to protect against potential interest rate increases.

You have confirmed that affordability and maintaining manageable monthly payments are key priorities.

You would like a mortgage term of approximately [TERM] years, balancing affordability with the total cost of borrowing.

You are comfortable with a monthly payment of approximately £[BUDGET].`,
      },
    },
    remortgage: {
      label: 'Remortgage',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      sections: {
        current_situation:
`You currently own your residential property valued at approximately £[PROPERTY VALUE], with an outstanding mortgage balance of £[LOAN AMOUNT].

Your existing mortgage deal is due to expire on [DATE], after which you would move onto your lender's standard variable rate.

You are currently employed as a [JOB TITLE], with an annual income of £[INCOME].${extra}${incomeNote}

The property remains your main residence.`,
        client_objectives:
`Your primary objective is to secure a new mortgage product to replace your existing deal and avoid moving onto a higher standard variable rate.

You have expressed a preference for a [2/3/5]-year fixed rate to provide certainty over your monthly payments.

You would like to maintain or reduce your current monthly payments where possible.

You are not looking to make any significant changes to your mortgage balance or term at this stage.`,
      },
    },
    remortgage_ab: {
      label: 'Remortgage — Additional Borrowing',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      sections: {
        current_situation:
`You currently own your residential property valued at approximately £[PROPERTY VALUE], with an outstanding mortgage balance of £[CURRENT LOAN].

You are looking to remortgage and raise additional borrowing of £[ADDITIONAL AMOUNT] for the purpose of [HOME IMPROVEMENTS / DEBT CONSOLIDATION / OTHER].

You are currently employed as a [JOB TITLE], with an annual income of £[INCOME].${extra}${incomeNote}

The property remains your main residence.`,
        client_objectives:
`Your primary objective is to secure a new mortgage that allows you to both replace your existing deal and release additional funds for [PURPOSE].

You have expressed a preference for a [2/3/5]-year fixed rate to provide stability in your monthly payments.

You would like to ensure that the additional borrowing remains affordable within your current financial circumstances.

You are aware that increasing your borrowing will increase your monthly payments and the total cost of borrowing over time.`,
      },
    },
    btl: {
      label: 'Buy-to-Let',
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      sections: {
        current_situation:
`You are looking to purchase a Buy-to-Let property for £[PROPERTY VALUE], with a deposit of £[DEPOSIT] sourced from [SOURCE OF FUNDS].

You are currently employed as a [JOB TITLE], a role you have held since [DATE], and you may also receive additional income from existing rental properties.${extra}

You are [MARITAL STATUS] with [NUMBER] dependants.

This purchase is being made through [estate agent / company structure if relevant] and is intended as an investment property.`,
        client_objectives:
`Your primary objective is to secure a competitive mortgage product that supports your investment strategy.

You have confirmed a preference for an interest-only mortgage arrangement in order to maximise rental yield.

You would like a [2/3/5]-year fixed rate product to provide stability in payments and protect against interest rate increases.

You are comfortable with a monthly payment of approximately £[BUDGET] and are aware of the risks associated with rental void periods and interest rate changes after the initial term.`,
      },
    },
    product_transfer: {
      label: 'Product Transfer',
      color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
      sections: {
        current_situation:
`You currently hold a mortgage with [LENDER], with an outstanding balance of £[LOAN AMOUNT], secured against your property valued at approximately £[PROPERTY VALUE].

Your existing mortgage deal is due to expire on [DATE], after which you would move onto the lender's standard variable rate.

You are not looking to change your lender, borrow additional funds, or make significant changes to your mortgage structure.`,
        client_objectives:
`Your primary objective is to secure a new product with your existing lender in order to avoid moving onto the standard variable rate.

You have expressed a preference for a [2/3/5]-year fixed rate to provide certainty over your monthly payments.

You are looking for a straightforward process with minimal underwriting and no changes to your current mortgage arrangements.

You would like to maintain or reduce your current monthly payments where possible.`,
      },
    },
  };
}

// ── Case Overview template factory ───────────────────────────────────────
function buildOverviewTemplates(applicants) {
  const applicantNote = applicants > 1
    ? `\n\nApplicant 2 is [SECOND APPLICANT NAME], employed as a [JOB TITLE 2] since [DATE 2], with an annual income of £[INCOME 2].${applicants > 2 ? '\n\nApplicant 3 is [THIRD APPLICANT NAME], employed as a [JOB TITLE 3] since [DATE 3], with an annual income of £[INCOME 3].' : ''}${applicants > 3 ? '\n\nApplicant 4 is [FOURTH APPLICANT NAME], employed as a [JOB TITLE 4] since [DATE 4], with an annual income of £[INCOME 4].' : ''}`
    : '';

  return {
    purchase_ftb: {
      label: 'Purchase — First-Time Buyer',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      overview:
`You are looking to purchase your first residential property for £[PROPERTY VALUE], with a deposit of £[DEPOSIT] from [SOURCE OF FUNDS]. Applicant is [employed / self-employed] as a [JOB TITLE] since [DATE].${applicantNote} You are [MARITAL STATUS] and have [NUMBER] dependants. You are purchasing this property through an estate agent and this will be your main residence.
Your priority is to secure a [RATE TYPE] rate at the lowest possible rate and monthly payment for the next [PRODUCT TERM] years to provide certainty over your payments.
You would like a repayment mortgage to reduce your outstanding balance over the term.
You would like a term that fits in with your budget of £[BUDGET]pm and you are aware that the longer the term the lower your payments will be, however you will pay more interest.
You would like a mortgage that offers free valuation.

TERM – You would like a term of [TERM] years as having looked at indicative quotes this term suits your budget but also only takes the term [X] year[s] past retirement.

PROPERTY – This is a [BEDROOMS]-bedroom [PROPERTY TYPE] which is [FREEHOLD / LEASEHOLD].

DEPOSIT – The deposit is coming from your [SOURCE OF FUNDS] which [have built up over time / was gifted by family / have built up over time and gift deposit from family].

FEES/COSTS – You have confirmed that if a fee is payable you would prefer to add it to the mortgage and you fully understand that by adding the fee to the mortgage you will pay interest on the fee over the whole term of the mortgage.

REPAYMENT – You would like a repayment mortgage as this will reduce your outstanding balance over the term.

RESEARCH – [RESEARCH - TO BE COMPLETED]

PROTECTION – With regards to protection they would like the mortgage approved first and once this has been done they are happy to discuss their protection needs. They fully understand the implications of not having adequate cover in place i.e. should something serious happen they could lose their home.`,
    },
    remortgage: {
      label: 'Remortgage',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      overview:
`You are looking to remortgage your property valued at approximately £[PROPERTY VALUE], with an outstanding mortgage balance of £[OUTSTANDING BALANCE] with [CURRENT LENDER]. Your current deal expires on [EXPIRY DATE] and you are looking to secure a new rate before reverting to the standard variable rate. Applicant is [employed / self-employed] as a [JOB TITLE] with an annual income of £[INCOME].${applicantNote} You are [MARITAL STATUS] and have [NUMBER] dependants.
Your priority is to secure a competitive [RATE TYPE] rate to replace your existing deal and avoid moving onto a higher standard variable rate.
You would like a [2/3/5]-year fixed rate to provide certainty over your monthly payments and keep your outgoings manageable.
You would like to maintain your current term of [TERM] years and keep your monthly payment at a similar level to your current payment.

TERM – You would like to maintain your current remaining term of [TERM] years.

PROPERTY – The property is your main residence valued at approximately £[PROPERTY VALUE] and is [FREEHOLD / LEASEHOLD].

FEES/COSTS – You have confirmed that if a fee is payable you would prefer to add it to the mortgage and you fully understand that by adding the fee to the mortgage you will pay interest on the fee over the whole term of the mortgage.

REPAYMENT – You would like to continue on a [repayment / interest only] basis.

RESEARCH – [RESEARCH - TO BE COMPLETED]

PROTECTION – With regards to protection they would like the mortgage approved first and once this has been done they are happy to discuss their protection needs. They fully understand the implications of not having adequate cover in place i.e. should something serious happen they could lose their home.`,
    },
    remortgage_ab: {
      label: 'Remortgage — Additional Borrowing',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      overview:
`You are looking to remortgage your property valued at approximately £[PROPERTY VALUE] and raise additional borrowing of £[ADDITIONAL AMOUNT] for the purpose of [PURPOSE OF FUNDS]. Your outstanding mortgage balance is £[OUTSTANDING BALANCE] with [CURRENT LENDER]. Your current deal expires on [EXPIRY DATE]. Applicant is [employed / self-employed] as a [JOB TITLE] with an annual income of £[INCOME].${applicantNote} You are [MARITAL STATUS] and have [NUMBER] dependants.
Your priority is to consolidate your existing mortgage and raise the additional funds for [PURPOSE OF FUNDS] in the most cost-effective way. You would like a [RATE TYPE] rate for the next [PRODUCT TERM] years to provide certainty over your payments.
You would like a term that fits in with your budget of £[BUDGET]pm and you are aware that by adding the additional borrowing to your mortgage you will pay interest on this amount over the full term.

TERM – You would like a term of [TERM] years as having looked at indicative quotes this term suits your budget but also only takes the term [X] year[s] past retirement.

PROPERTY – The property is your main residence valued at approximately £[PROPERTY VALUE] and is [FREEHOLD / LEASEHOLD].

FEES/COSTS – You have confirmed that if a fee is payable you would prefer to add it to the mortgage and you fully understand that by adding the fee to the mortgage you will pay interest on the fee over the whole term of the mortgage.

REPAYMENT – You would like a [repayment / interest only] mortgage.

RESEARCH – [RESEARCH - TO BE COMPLETED]

PROTECTION – With regards to protection they would like the mortgage approved first and once this has been done they are happy to discuss their protection needs. They fully understand the implications of not having adequate cover in place i.e. should something serious happen they could lose their home.`,
    },
    btl: {
      label: 'Buy-to-Let',
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      overview:
`You are looking to purchase a BTL property for £[PROPERTY VALUE] with a deposit of £[DEPOSIT] from [SOURCE OF FUNDS]. Applicant is [employed / self-employed] as a [JOB TITLE] since [DATE]. Applicant also has [EXISTING BTL PORTFOLIO NOTE]. Applicant is [MARITAL STATUS] and has [NUMBER] dependants.${applicantNote} You are purchasing this property through an estate agent.
Your priority is to secure a fixed rate at the lowest possible rate and monthly payment for the next [PRODUCT TERM] years as to avoid any rate spikes and make an informed decision after the initial period.
You would like an interest only mortgage as this would maximise rental yield.
You would like a term that fits in with your budget of £[BUDGET]pm anything lower would be great, and you are aware that the longer the term the lower your payments will be, however you will pay more interest.
You would like a mortgage that offers free valuation.

TERM – You would like a term of [TERM] years as having looked at indicative quotes this term suits your budget but also only takes the term [X] year[s] past retirement.

PROPERTY – This is a [BEDROOMS]-bedroom [PROPERTY TYPE] which is [FREEHOLD / LEASEHOLD].

DEPOSIT – The deposit is coming from your [SOURCE OF FUNDS] which [have built up over time / was gifted by family / have built up over time and gift deposit from family].

FEES/COSTS – You have confirmed that if a fee is payable you would prefer to add it to the mortgage and you fully understand that by adding the fee to the mortgage you will pay interest on the fee over the whole term of the mortgage.

REPAYMENT – You would like an interest only mortgage as this would maximise rental yield.

RESEARCH – [RESEARCH - TO BE COMPLETED]

PROTECTION – With regards to protection they would like the mortgage approved first and once this has been done they are happy to discuss their protection needs. They fully understand the implications of not having adequate cover in place i.e. should something serious happen they could lose their home.`,
    },
    product_transfer: {
      label: 'Product Transfer',
      color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
      overview:
`You currently hold a mortgage with [LENDER] with an outstanding balance of £[OUTSTANDING BALANCE], secured against your property valued at approximately £[PROPERTY VALUE]. Your existing deal expires on [EXPIRY DATE] and you are not looking to change your lender, borrow additional funds, or make any significant changes to your mortgage structure. Applicant is [employed / self-employed] as a [JOB TITLE] with an annual income of £[INCOME].${applicantNote} You are [MARITAL STATUS] and have [NUMBER] dependants.
Your priority is to secure a new product with your existing lender to avoid moving onto the standard variable rate. You have expressed a preference for a [RATE TYPE] rate for [PRODUCT TERM] years to provide certainty over your payments.
You would like to maintain your current monthly payment of approximately £[CURRENT PAYMENT]pm.

TERM – You would like to maintain your current remaining term of [TERM] years.

PROPERTY – The property is your main residence valued at approximately £[PROPERTY VALUE] and is [FREEHOLD / LEASEHOLD].

FEES/COSTS – You have confirmed that if a fee is payable you would prefer to add it to the mortgage.

REPAYMENT – You would like to continue on a [repayment / interest only] basis.

RESEARCH – [RESEARCH - TO BE COMPLETED]

PROTECTION – With regards to protection they would like the mortgage approved first and once this has been done they are happy to discuss their protection needs. They fully understand the implications of not having adequate cover in place i.e. should something serious happen they could lose their home.`,
    },
  };
}

const SUITABILITY_SECTION_LABELS = {
  current_situation: 'Current Situation',
  client_objectives: 'Client Objectives',
};

// ── Copy button ───────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button size="sm" variant="outline" onClick={handle}
      className="gap-1.5 border-slate-400 dark:border-slate-500 text-xs">
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
}

// ── Shared screenshot upload panel ────────────────────────────────────────
function ScreenshotPanel({ screenshots, onAdd, onRemove, onClearAll, fileRef }) {
  return (
    <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-700 dark:text-slate-300">Screenshots</CardTitle>
          {screenshots.length > 0 && (
            <Button size="sm" variant="ghost" onClick={onClearAll} className="text-xs text-slate-500 gap-1">
              <X className="h-3 w-3" /> Clear all
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Screenshots are shared — upload once, use for both Suitability and Case Overview extraction
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-5 text-center cursor-pointer hover:border-red-400 dark:hover:border-red-500 transition-colors"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onAdd(e.dataTransfer.files); }}>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => onAdd(e.target.files)} />
          <ImagePlus className="h-7 w-7 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Drop screenshots here or click to select</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">PNG, JPG, WEBP — select multiple at once</p>
        </div>

        {screenshots.length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {screenshots.map((s) => (
              <div key={s.name} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                <img src={s.preview} alt={s.name} className="w-full h-20 object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button onClick={() => onRemove(s.name)} className="bg-red-600 text-white rounded-full p-1">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="p-1 bg-slate-50 dark:bg-slate-900">
                  <p className="text-xs text-slate-400 truncate">{s.name}</p>
                </div>
              </div>
            ))}
            <div
              className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg h-20 flex flex-col items-center justify-center cursor-pointer hover:border-red-400 transition-colors"
              onClick={() => fileRef.current?.click()}>
              <ImagePlus className="h-4 w-4 text-slate-400 mb-1" />
              <p className="text-xs text-slate-400">Add more</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Extraction result cards ────────────────────────────────────────────────
function ExtractionResult({ result, TEMPLATES, onChange, sectionKeys }) {
  if (!result) return null;
  return (
    <div className="space-y-4">
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Extraction Result</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`border-0 text-xs ${
                result.confidence === 'high'   ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                result.confidence === 'medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                                                 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}>
                {result.confidence} confidence
              </Badge>
              {result.detected_type && (
                <Badge className={`border-0 text-xs ${TEMPLATES[result.detected_type]?.color}`}>
                  {TEMPLATES[result.detected_type]?.label}
                </Badge>
              )}
            </div>
          </div>
          {result.detection_reason && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{result.detection_reason}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {result.applicants?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">Applicant Details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.applicants.map((ap, i) => (
                  <div key={i} className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">
                      Applicant {i + 1}{ap.name ? ` — ${ap.name}` : ''}
                    </p>
                    {[['Job title', ap.job_title], ['Employed since', ap.employment_start],
                      ['Income', ap.income], ['Employment type', ap.employment_type]]
                      .filter(([, v]) => v && v !== '...')
                      .map(([label, val]) => (
                        <div key={label} className="flex justify-between text-xs py-0.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                          <span className="text-slate-400 dark:text-slate-500">{label}</span>
                          <span className="text-slate-700 dark:text-slate-300 font-medium">{val}</span>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.extracted && Object.entries(result.extracted).filter(([, v]) => v && v !== '...').length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">Case Details</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(result.extracted).filter(([, v]) => v && v !== '...').map(([k, v]) => (
                  <div key={k} className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-2">
                    <p className="text-xs text-slate-400 dark:text-slate-500 capitalize">{k.replace(/_/g, ' ')}</p>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mt-0.5 truncate">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {sectionKeys.map((key) => (
        result[key] && (
          <Card key={key} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {key === 'overview' ? 'Case Overview' :
                   key === 'current_situation' ? 'Current Situation' : 'Client Objectives'}
                </CardTitle>
                <CopyButton text={result[key]} />
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={result[key]}
                onChange={(e) => onChange(key, e.target.value)}
                rows={key === 'overview' ? 18 : 8}
                className="border-slate-400 dark:border-slate-500 text-sm leading-relaxed resize-y"
              />
            </CardContent>
          </Card>
        )
      ))}
    </div>
  );
}


// ── Your Needs answer bank ────────────────────────────────────────────────
const YOUR_NEEDS_QUESTIONS = [
  {
    question: "How would it affect your household if your monthly payments increased significantly?",
    answers: [
      "Client has sufficient income and savings so would not affect the client.",
    ],
  },
  {
    question: "Please provide details (repayment provisions at end of term)",
    note: "Interest Only Question",
    answers: [
      "Sale of mortgaged property.",
    ],
  },
  {
    question: "Over what period and why? Why did you discount any other lender?",
    answers: [
      "Over a period of 2 years as the client wants the security of a fixed rate for a shorter period before reviewing their options.",
      "Over a period of 5 years as the client doesn't want to worry about any rate changes or spikes.",
    ],
  },
  {
    question: "Please provide a reason for the term length requested",
    answers: [
      "X years was chosen as this was the client's request within their budget.",
    ],
  },
  {
    question: "How do you intend to continue paying the mortgage after the state retirement age?",
    answers: [
      "Rental income.",
    ],
  },
  {
    question: "Reason Why (continuing to pay past retirement age)",
    answers: [
      "Client will have rental income so will have enough money to cover payments and extra.",
    ],
  },
  {
    question: "Reason Why (minimise monthly payments / interest only / long term)",
    answers: [
      "Maximum rental yield.",
    ],
  },
];

function QuickCopyPill({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      onClick={handle}
      className={`text-left w-full px-4 py-2.5 rounded-lg text-sm border transition-all group flex items-start gap-3 ${
        copied
          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
          : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300 hover:border-red-400 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/10'
      }`}
    >
      <span className="flex-shrink-0 mt-0.5">
        {copied
          ? <Check className="h-3.5 w-3.5 text-green-600" />
          : <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-red-500" />
        }
      </span>
      <span className="leading-relaxed">{text}</span>
    </button>
  );
}

function YourNeedsTab() {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (i) => setExpanded(e => ({ ...e, [i]: !e[i] }));

  return (
    <div className="space-y-3">
      {YOUR_NEEDS_QUESTIONS.map((q, i) => {
        const isOpen = expanded[i] !== false; // default open
        return (
          <Card key={i} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle(i)}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-snug">
                    {q.question}
                  </CardTitle>
                </div>
                <div className="flex-shrink-0">
                  {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </div>
            </CardHeader>
            {isOpen && (
              <CardContent className="pt-0 space-y-2">
                {q.note && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-1.5 rounded-lg mb-2">
                    {q.note}
                  </p>
                )}
                {q.answers.map((answer, j) => (
                  <QuickCopyPill key={j} text={answer} />
                ))}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function Suitability() {
  const [activeType, setActiveType] = useState('purchase_ftb');
  const [applicants, setApplicants] = useState(1);

  // Suitability template edits
  const [editedSuitability, setEditedSuitability] = useState(() => {
    try { const s = localStorage.getItem('sl_templates_v3'); return s ? JSON.parse(s) : {}; }
    catch { return {}; }
  });
  const [expandedSuitability, setExpandedSuitability] = useState({ current_situation: true, client_objectives: true });

  // Case Overview template edits
  const [editedOverview, setEditedOverview] = useState(() => {
    try { const s = localStorage.getItem('co_templates_v2'); return s ? JSON.parse(s) : {}; }
    catch { return {}; }
  });
  const [overviewExpanded, setOverviewExpanded] = useState(true);

  // Shared screenshots
  const [screenshots, setScreenshots] = useState([]);
  const fileRef = useRef();

  // Suitability AI state
  const [suitLoading, setSuitLoading] = useState(false);
  const [suitResult, setSuitResult] = useState(null);
  const [suitError, setSuitError] = useState(null);

  // Case Overview AI state
  const [ovLoading, setOvLoading] = useState(false);
  const [ovResult, setOvResult] = useState(null);
  const [ovError, setOvError] = useState(null);

  const SUIT_TEMPLATES = buildSuitabilityTemplates(applicants);
  const OV_TEMPLATES = buildOverviewTemplates(applicants);

  // ── Suitability template helpers ────────────────────────────────────────
  const suitKey = (type, section) => `${type}_${section}_${applicants}app`;
  const getSuit = (type, section) => editedSuitability[suitKey(type, section)] ?? SUIT_TEMPLATES[type].sections[section];
  const saveSuit = (type, section, value) => {
    const updated = { ...editedSuitability, [suitKey(type, section)]: value };
    setEditedSuitability(updated);
    localStorage.setItem('sl_templates_v3', JSON.stringify(updated));
  };
  const resetSuit = (type, section) => {
    const updated = { ...editedSuitability };
    delete updated[suitKey(type, section)];
    setEditedSuitability(updated);
    localStorage.setItem('sl_templates_v3', JSON.stringify(updated));
  };
  const isSuitEdited = (type, section) => !!editedSuitability[suitKey(type, section)];

  // ── Overview template helpers ────────────────────────────────────────────
  const ovKey = (type) => `${type}_overview_${applicants}app`;
  const getOv = (type) => editedOverview[ovKey(type)] ?? OV_TEMPLATES[type].overview;
  const saveOv = (type, value) => {
    const updated = { ...editedOverview, [ovKey(type)]: value };
    setEditedOverview(updated);
    localStorage.setItem('co_templates_v2', JSON.stringify(updated));
  };
  const resetOv = (type) => {
    const updated = { ...editedOverview };
    delete updated[ovKey(type)];
    setEditedOverview(updated);
    localStorage.setItem('co_templates_v2', JSON.stringify(updated));
  };
  const isOvEdited = (type) => !!editedOverview[ovKey(type)];

  // ── Screenshot management ────────────────────────────────────────────────
  const handleFiles = (fileList) => {
    setSuitResult(null); setSuitError(null);
    setOvResult(null); setOvError(null);
    Array.from(fileList).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshots(prev => {
          if (prev.find(s => s.name === file.name)) return prev;
          return [...prev, { file, preview: e.target.result, name: file.name }];
        });
      };
      reader.readAsDataURL(file);
    });
  };
  const removeScreenshot = (name) => {
    setScreenshots(prev => prev.filter(s => s.name !== name));
    setSuitResult(null); setOvResult(null);
  };
  const clearAll = () => { setScreenshots([]); setSuitResult(null); setOvResult(null); };

  // ── Suitability extraction ────────────────────────────────────────────────
  const runSuitAI = async () => {
    if (screenshots.length === 0) return;
    setSuitLoading(true); setSuitError(null); setSuitResult(null);
    try {
      const formData = new FormData();
      screenshots.forEach((s) => formData.append('files', s.file));
      formData.append('applicants', String(applicants));
      formData.append('mortgage_type', activeType);
      formData.append('current_situation_template', SUIT_TEMPLATES[activeType]?.sections.current_situation || '');
      formData.append('client_objectives_template', SUIT_TEMPLATES[activeType]?.sections.client_objectives || '');
      const result = await extractAPI.extractSuitability(formData);
      const parsed = result.data || result;
      setSuitResult(parsed);
      if (parsed.detected_type && SUIT_TEMPLATES[parsed.detected_type]) setActiveType(parsed.detected_type);
    } catch (err) {
      setSuitError(`Extraction failed: ${err.message}. Check your screenshots are clear and try again.`);
    } finally {
      setSuitLoading(false);
    }
  };



  // ── Case Overview extraction ──────────────────────────────────────────────
  const runOvAI = async () => {
    if (screenshots.length === 0) return;
    setOvLoading(true); setOvError(null); setOvResult(null);
    try {
      const formData = new FormData();
      screenshots.forEach((s) => formData.append('files', s.file));
      formData.append('applicants', String(applicants));
      formData.append('mortgage_type', activeType);
      formData.append('overview_template', OV_TEMPLATES[activeType]?.overview || '');
      const result = await extractAPI.extractCaseOverview(formData);
      const parsed = result.data || result;
      setOvResult(parsed);
      if (parsed.detected_type && OV_TEMPLATES[parsed.detected_type]) setActiveType(parsed.detected_type);
    } catch (err) {
      setOvError(`Extraction failed: ${err.message}. Check your screenshots are clear and try again.`);
    } finally {
      setOvLoading(false);
    }
  };



  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Suitability &amp; Case Overview
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Pre-written suitability letter templates and case overview copy — paste directly into your network system
        </p>
      </div>

      {/* Applicant selector */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Number of applicants:</span>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map(n => (
                <button key={n} onClick={() => setApplicants(n)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium border transition-all ${
                    applicants === n
                      ? 'border-red-500 bg-red-600 text-white'
                      : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-red-400 dark:hover:border-red-500'
                  }`}>
                  {n}
                </button>
              ))}
            </div>
            {applicants > 1 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-1 rounded-full">
                Templates updated for {applicants} applicants
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="suitability" className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-lg inline-flex h-auto">
          <TabsTrigger value="suitability">Suitability Letter</TabsTrigger>
          <TabsTrigger value="overview">Case Overview</TabsTrigger>
          <TabsTrigger value="yourneeds">Your Needs</TabsTrigger>
          <TabsTrigger value="screenshots" className="gap-2">
            <ImagePlus className="h-4 w-4" />
            Screenshots{screenshots.length > 0 && ` (${screenshots.length})`}
          </TabsTrigger>
        </TabsList>

        {/* ── SUITABILITY LETTER ──────────────────────────────────────── */}
        <TabsContent value="suitability" className="mt-6 space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Select a mortgage type, copy each section into your network system. Click into any text area to edit — changes save automatically.
          </p>

          <div className="flex flex-wrap gap-2">
            {Object.entries(SUIT_TEMPLATES).map(([key, tpl]) => (
              <button key={key} onClick={() => setActiveType(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  activeType === key
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
                }`}>
                {tpl.label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {Object.entries(SUITABILITY_SECTION_LABELS).map(([sectionKey, sectionLabel]) => {
              const text = getSuit(activeType, sectionKey);
              const edited = isSuitEdited(activeType, sectionKey);
              const isOpen = expandedSuitability[sectionKey];
              return (
                <Card key={sectionKey} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <CardTitle className="text-base">{sectionLabel}</CardTitle>
                        <Badge className={`text-xs border-0 ${SUIT_TEMPLATES[activeType].color}`}>
                          {SUIT_TEMPLATES[activeType].label}
                        </Badge>
                        {applicants > 1 && (
                          <Badge className="text-xs border-0 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            {applicants} applicants
                          </Badge>
                        )}
                        {edited && (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs border-0">
                            Customised
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <CopyButton text={text} />
                        {edited && (
                          <Button size="sm" variant="ghost" onClick={() => resetSuit(activeType, sectionKey)}
                            className="text-xs text-slate-500 gap-1">
                            <RefreshCw className="h-3 w-3" /> Reset
                          </Button>
                        )}
                        <Button size="sm" variant="ghost"
                          onClick={() => setExpandedSuitability(e => ({ ...e, [sectionKey]: !e[sectionKey] }))}>
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {isOpen && (
                    <CardContent>
                      <Textarea value={text} onChange={(e) => saveSuit(activeType, sectionKey, e.target.value)}
                        rows={8} className="border-slate-400 dark:border-slate-500 text-sm leading-relaxed resize-y font-normal" />
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                        Tokens in <span className="font-mono">[BRACKETS]</span> are placeholders — replace with real values before copying.
                      </p>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Suitability AI section */}
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">Auto-fill from screenshots</span>
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            </div>
            {screenshots.length === 0 ? (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400">
                <ImagePlus className="h-4 w-4 flex-shrink-0" />
                Upload screenshots in the Screenshots tab first, then come back here to auto-fill.
              </div>
            ) : (
              <Button onClick={runSuitAI} disabled={suitLoading}
                className="w-full bg-red-600 hover:bg-red-700 text-white gap-2">
                {suitLoading
                  ? <><RefreshCw className="h-4 w-4 animate-spin" />Reading {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''}...</>
                  : <><Wand2 className="h-4 w-4" />Auto-fill suitability from {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''} · {applicants} applicant{applicants > 1 ? 's' : ''}</>
                }
              </Button>
            )}
            {suitError && (
              <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{suitError}</div>
            )}
            {suitResult && (
              <ExtractionResult
                result={suitResult}
                TEMPLATES={SUIT_TEMPLATES}
                onChange={(key, val) => setSuitResult(prev => ({ ...prev, [key]: val }))}
                sectionKeys={['current_situation', 'client_objectives']}
              />
            )}
          </div>
        </TabsContent>

        {/* ── CASE OVERVIEW ───────────────────────────────────────────── */}
        <TabsContent value="overview" className="mt-6 space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Select a mortgage type, then copy the full case overview into your network system. The RESEARCH section is left blank for you to complete manually for each client.
          </p>

          <div className="flex flex-wrap gap-2">
            {Object.entries(OV_TEMPLATES).map(([key, tpl]) => (
              <button key={key} onClick={() => setActiveType(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  activeType === key
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
                }`}>
                {tpl.label}
              </button>
            ))}
          </div>

          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <CardTitle className="text-base">Case Overview</CardTitle>
                  <Badge className={`text-xs border-0 ${OV_TEMPLATES[activeType].color}`}>
                    {OV_TEMPLATES[activeType].label}
                  </Badge>
                  {applicants > 1 && (
                    <Badge className="text-xs border-0 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {applicants} applicants
                    </Badge>
                  )}
                  {isOvEdited(activeType) && (
                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs border-0">
                      Customised
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <CopyButton text={getOv(activeType)} />
                  {isOvEdited(activeType) && (
                    <Button size="sm" variant="ghost" onClick={() => resetOv(activeType)}
                      className="text-xs text-slate-500 gap-1">
                      <RefreshCw className="h-3 w-3" /> Reset
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setOverviewExpanded(e => !e)}>
                    {overviewExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            {overviewExpanded && (
              <CardContent>
                <Textarea
                  value={getOv(activeType)}
                  onChange={(e) => saveOv(activeType, e.target.value)}
                  rows={20}
                  className="border-slate-400 dark:border-slate-500 text-sm leading-relaxed resize-y font-normal"
                />
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Tokens in <span className="font-mono">[BRACKETS]</span> are placeholders. The RESEARCH section is intentionally left blank — complete this manually for each client.
                </p>
              </CardContent>
            )}
          </Card>

          {/* Case Overview AI section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">Auto-fill from screenshots</span>
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            </div>
            {screenshots.length === 0 ? (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400">
                <ImagePlus className="h-4 w-4 flex-shrink-0" />
                Upload screenshots in the Screenshots tab first, then come back here to auto-fill.
              </div>
            ) : (
              <Button onClick={runOvAI} disabled={ovLoading}
                className="w-full bg-red-600 hover:bg-red-700 text-white gap-2">
                {ovLoading
                  ? <><RefreshCw className="h-4 w-4 animate-spin" />Reading {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''}...</>
                  : <><Wand2 className="h-4 w-4" />Auto-fill case overview from {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''} · {applicants} applicant{applicants > 1 ? 's' : ''}</>
                }
              </Button>
            )}
            {ovError && (
              <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{ovError}</div>
            )}
            {ovResult && (
              <ExtractionResult
                result={ovResult}
                TEMPLATES={OV_TEMPLATES}
                onChange={(key, val) => setOvResult(prev => ({ ...prev, [key]: val }))}
                sectionKeys={['overview']}
              />
            )}
          </div>
        </TabsContent>


        {/* ── YOUR NEEDS ──────────────────────────────────────────────── */}
        <TabsContent value="yourneeds" className="mt-6 space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pre-written answers for the Your Needs section of your network system. Click any sentence to copy it instantly.
          </p>
          <YourNeedsTab />
        </TabsContent>

        {/* ── SCREENSHOTS TAB ─────────────────────────────────────────── */}
        <TabsContent value="screenshots" className="mt-6 space-y-4">
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
            <Wand2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Upload your client screenshots once here — then use the Auto-fill buttons on both the Suitability Letter and Case Overview tabs to extract from the same screenshots.
            </p>
          </div>
          <ScreenshotPanel
            screenshots={screenshots}
            onAdd={handleFiles}
            onRemove={removeScreenshot}
            onClearAll={clearAll}
            fileRef={fileRef}
          />
          {screenshots.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button onClick={runSuitAI} disabled={suitLoading}
                className="bg-red-600 hover:bg-red-700 text-white gap-2">
                {suitLoading
                  ? <><RefreshCw className="h-4 w-4 animate-spin" />Extracting suitability...</>
                  : <><Wand2 className="h-4 w-4" />Auto-fill Suitability Letter</>
                }
              </Button>
              <Button onClick={runOvAI} disabled={ovLoading}
                className="bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white gap-2">
                {ovLoading
                  ? <><RefreshCw className="h-4 w-4 animate-spin" />Extracting case overview...</>
                  : <><Wand2 className="h-4 w-4" />Auto-fill Case Overview</>
                }
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
